import { useState, useEffect } from 'react';
import { Clock, X, Zap } from 'lucide-react';
import { useTheme } from './ThemeContext';

/**
 * Persistent trial-status banner. Shows for any company in trial that hasn't
 * started a paid subscription. Demo accounts are skipped — they're not real trials.
 *
 * Visibility rules:
 *  - Hide if company is missing, is_demo, or already has stripe_subscription_id
 *  - Show if subscription_status is 'trialing' OR trial_ends_at is in the future
 *  - User-dismissed banners stay hidden for the rest of the browser session
 */
export default function TrialBanner({ companyData, onUpgradeClick }) {
  const { colors } = useTheme();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem('trial_banner_dismissed') === '1');
  }, []);

  if (!companyData) return null;
  if (companyData.is_demo) return null;
  if (companyData.stripe_subscription_id) return null;
  if (dismissed) return null;

  const trialEndsAt = companyData.trial_ends_at ? new Date(companyData.trial_ends_at) : null;
  const status = companyData.subscription_status;
  const isTrialing = status === 'trialing' || (trialEndsAt && trialEndsAt > new Date());
  if (!isTrialing) return null;

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / msPerDay))
    : null;
  const expired = trialEndsAt && trialEndsAt < new Date();

  // Style intensifies as trial nears end
  const urgent = expired || (daysLeft !== null && daysLeft <= 3);
  const accent = expired ? '#EF4444' : urgent ? '#F59E0B' : colors.accent;

  const message = expired
    ? 'Your free trial has ended'
    : daysLeft === null
      ? 'You’re on a free trial'
      : daysLeft === 0
        ? 'Trial ends today'
        : daysLeft === 1
          ? 'Trial ends tomorrow'
          : `${daysLeft} days left in your free trial`;

  const handleDismiss = () => {
    sessionStorage.setItem('trial_banner_dismissed', '1');
    setDismissed(true);
  };

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.6rem 1rem',
        background: `${accent}15`,
        borderBottom: `1px solid ${accent}40`,
        color: colors.text,
        fontSize: '0.9rem',
      }}
    >
      <Clock size={16} color={accent} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ color: accent, fontWeight: 600 }}>{message}</strong>
        {!expired && (
          <span style={{ color: colors.textMuted, marginLeft: '0.5rem' }}>
            · Upgrade anytime to keep going
          </span>
        )}
      </div>
      <button
        onClick={onUpgradeClick}
        style={{
          padding: '0.4rem 0.85rem',
          background: accent,
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.35rem',
          flexShrink: 0,
        }}
      >
        <Zap size={14} />
        {expired ? 'Reactivate' : 'Upgrade'}
      </button>
      {!expired && (
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          style={{
            padding: '0.25rem',
            background: 'transparent',
            border: 'none',
            color: colors.textMuted,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
